import { PermissionGate } from "@/components/common/PermissionGate"
import { toast } from "@/components/common/Toast"
import { isLimitError } from "@/utils/limitErrors"
import { Filter, Plus, Settings2, Tag, Trash2, X } from "lucide-react"
import * as React from "react"
import type { DateRange } from "react-day-picker"

import { SearchField } from "@/components/common/SearchField/SearchField"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { PERMISSIONS } from "@/constants/permissions"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import {
  useCreateKnowledgeBaseTag,
  useDeleteKnowledgeBaseTag,
  useUpdateKnowledgeBaseTag,
} from "@/hooks/useKnowledgeBaseCategories/useKnowledgeBaseCategories"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import type { KnowledgeBaseCategory } from "@/types/knowledgebaseCategories"
import { knowledgeBaseErrorKey } from "@/utils/knowledgeBaseErrors"
import { getLocalizedLabel } from "@/utils/localizedLabel"

import { Switch } from "../../ui/switch"
import { CreateKnowledgeBaseCategoryDialog } from "./CreateKnowledgeBaseCategoryDialog"
import { DateRangeFilter } from "./DateRangeFilter"
import { FacetedFilter, type FacetedFilterOption } from "./FacetedFilter"

export interface KnowledgebaseFilters {
  search: string
  autoTagStatus: string[]
  tags: Record<string, string[]> // categoryId → tagId[]
  createdAtRange: DateRange | undefined
  excludeExpiredDocs: boolean
}

export const DEFAULT_FILTERS: KnowledgebaseFilters = {
  search: "",
  autoTagStatus: [],
  tags: {},
  createdAtRange: undefined,
  excludeExpiredDocs: false,
}

interface KnowledgebaseFilterBarProps {
  filters: KnowledgebaseFilters
  onFiltersChange: (filters: KnowledgebaseFilters) => void
  categories: KnowledgeBaseCategory[]
  showAllTags: boolean
  onShowAllTagsChange: (showAllTags: boolean) => void
  selectedCount?: number
  selectableCount?: number
  onSelectAll?: () => void
  onDeselectAll?: () => void
  onDeleteSelected?: () => void
  onBulkTag?: () => void
  isFolderSelected?: boolean
  onFolderReset?: () => void
}

type FilterBarLayoutProps = React.HTMLAttributes<HTMLDivElement>

function FilterBarRow({ className, ...props }: FilterBarLayoutProps) {
  return (
    <div
      className={cn("flex min-h-8 flex-wrap items-center gap-2", className)}
      {...props}
    />
  )
}

function FilterBarActions({ className, ...props }: FilterBarLayoutProps) {
  return (
    <div
      className={cn("flex flex-1 flex-wrap items-center gap-2", className)}
      {...props}
    />
  )
}

const COMPACT_ICON_BUTTON_CLASS_NAME = "size-8 shrink-0"

export function KnowledgebaseFilterBar({
  filters,
  onFiltersChange,
  categories,
  showAllTags,
  onShowAllTagsChange,
  selectedCount = 0,
  selectableCount = 0,
  onSelectAll,
  onDeselectAll,
  onDeleteSelected,
  onBulkTag,
  isFolderSelected = false,
  onFolderReset,
}: KnowledgebaseFilterBarProps) {
  const { translate, currentLanguage } = useTranslation()
  const isTaggingEnabled = useFeatureFlag("KB_TAGGING_FLOW")
  const [isCreateCategoryDialogOpen, setIsCreateCategoryDialogOpen] =
    React.useState(false)
  const [isFiltersVisible, setIsFiltersVisible] = React.useState(false)
  const [searchInput, setSearchInput] = React.useState(filters.search)
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const latestFiltersRef = React.useRef(filters)
  const { mutateAsync: createTag, isPending: isCreatingTag } =
    useCreateKnowledgeBaseTag()
  const { mutateAsync: updateTag } = useUpdateKnowledgeBaseTag()
  const { mutateAsync: deleteTag } = useDeleteKnowledgeBaseTag()

  React.useEffect(() => {
    latestFiltersRef.current = filters
  }, [filters])

  React.useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [])

  // Debounced search
  const handleSearchChange = React.useCallback(
    (value: string) => {
      setSearchInput(value)
      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => {
        onFiltersChange({ ...latestFiltersRef.current, search: value })
      }, 300)
    },
    [onFiltersChange],
  )

  // Keep local search in sync if filters reset externally
  React.useEffect(() => {
    setSearchInput(filters.search)
  }, [filters.search])

  const hasActiveTagFilters =
    isTaggingEnabled &&
    Object.values(filters.tags).some((tagIds) => tagIds.length > 0)

  const hasActivePanelFilters =
    filters.autoTagStatus.length > 0 ||
    filters.createdAtRange?.from != null ||
    filters.createdAtRange?.to != null ||
    hasActiveTagFilters

  React.useEffect(() => {
    if (hasActivePanelFilters) {
      setIsFiltersVisible(true)
    }
  }, [hasActivePanelFilters])

  const hasActiveFilters =
    filters.search !== "" ||
    filters.excludeExpiredDocs ||
    filters.autoTagStatus.length > 0 ||
    filters.createdAtRange?.from != null ||
    filters.createdAtRange?.to != null ||
    hasActiveTagFilters ||
    isFolderSelected

  const hasBulkSelection =
    selectedCount > 0 &&
    onSelectAll != null &&
    onDeselectAll != null &&
    onDeleteSelected != null
  const isAllSelectableSelected =
    selectableCount > 0 && selectedCount === selectableCount

  const resetAll = React.useCallback(() => {
    onFiltersChange(DEFAULT_FILTERS)
    onFolderReset?.()
  }, [onFiltersChange, onFolderReset])

  const handleCreateTag = React.useCallback(
    async (category: KnowledgeBaseCategory, tagName: string) => {
      if (!tagName.trim()) {
        toast.error(translate("KNOWLEDGEBASE/NAME_INVALID_ERROR"))
        return
      }

      try {
        const createdTag = await createTag({
          categoryId: category.id,
          data: { displayNameEn: tagName.trim() },
        })

        const currentFilters = latestFiltersRef.current
        const selectedTags = currentFilters.tags[category.id] ?? []

        onFiltersChange({
          ...currentFilters,
          tags: {
            ...currentFilters.tags,
            [category.id]: Array.from(
              new Set([...selectedTags, createdTag.id]),
            ),
          },
        })

        toast.success(
          translate("KNOWLEDGEBASE/TAG_CREATE_SUCCESS", { tag: tagName }),
        )
      } catch (error) {
        if (!isLimitError(error)) {
          toast.error(
            translate(
              knowledgeBaseErrorKey(error, "KNOWLEDGEBASE/TAG_CREATE_ERROR"),
              { name: tagName },
            ),
          )
        }
      }
    },
    [createTag, onFiltersChange, translate],
  )

  const handleRenameTag = React.useCallback(
    async (
      categoryId: string,
      tagId: string,
      currentLabel: string,
      nextLabel: string,
    ) => {
      const trimmedLabel = nextLabel.trim()

      if (!trimmedLabel || trimmedLabel === currentLabel) {
        return
      }

      try {
        await updateTag({
          categoryId,
          tagId,
          data: { displayNameEn: trimmedLabel },
        })
        toast.success(
          translate("KNOWLEDGEBASE/TAG_UPDATE_SUCCESS", {
            tag: trimmedLabel,
          }),
        )
      } catch (error) {
        if (!isLimitError(error)) {
          toast.error(
            translate(
              knowledgeBaseErrorKey(error, "KNOWLEDGEBASE/TAG_UPDATE_ERROR"),
              { name: trimmedLabel },
            ),
          )
        }
        throw new Error("rename failed", { cause: error })
      }
    },
    [translate, updateTag],
  )

  const handleDeleteTag = React.useCallback(
    async (categoryId: string, tagId: string, label: string) => {
      try {
        await deleteTag({ categoryId, tagId })

        const currentFilters = latestFiltersRef.current
        const selectedTags = currentFilters.tags[categoryId] ?? []

        onFiltersChange({
          ...currentFilters,
          tags: {
            ...currentFilters.tags,
            [categoryId]: selectedTags.filter(
              (selectedTagId) => selectedTagId !== tagId,
            ),
          },
        })

        toast.success(
          translate("KNOWLEDGEBASE/TAG_DELETE_SUCCESS", { tag: label }),
        )
      } catch {
        toast.error(translate("KNOWLEDGEBASE/TAG_DELETE_ERROR"))
        throw new Error("delete failed")
      }
    },
    [deleteTag, onFiltersChange, translate],
  )

  return (
    <div className="flex flex-col gap-2">
      {isTaggingEnabled && (
        <CreateKnowledgeBaseCategoryDialog
          open={isCreateCategoryDialogOpen}
          onOpenChange={setIsCreateCategoryDialogOpen}
        />
      )}

      <FilterBarRow>
        <FilterBarActions>
          <SearchField
            value={searchInput}
            onChange={handleSearchChange}
            placeholder={translate("KNOWLEDGEBASE/SEARCH_PLACEHOLDER")}
            onClear={() => handleSearchChange("")}
          />
          {isTaggingEnabled && (
            <>
              <Button
                variant={isFiltersVisible ? "secondary" : "outline"}
                size="icon"
                className={COMPACT_ICON_BUTTON_CLASS_NAME}
                aria-label={translate("KNOWLEDGEBASE/FILTER_BUTTON")}
                title={translate("KNOWLEDGEBASE/FILTER_BUTTON")}
                onClick={() => setIsFiltersVisible((current) => !current)}
              >
                <Filter className="size-4" />
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className={COMPACT_ICON_BUTTON_CLASS_NAME}
                    aria-label={translate("SETTINGS/TITLE")}
                    title={translate("SETTINGS/TITLE")}
                  >
                    <Settings2 className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="flex items-center justify-between gap-3 px-2 py-1.5">
                    <span className="text-sm text-popover-foreground">
                      {translate("KNOWLEDGEBASE/SHOW_ALL_TAGS")}
                    </span>
                    <Switch
                      checked={showAllTags}
                      onCheckedChange={onShowAllTagsChange}
                      aria-label={translate("KNOWLEDGEBASE/SHOW_ALL_TAGS")}
                    />
                  </div>
                  <div className="flex items-center justify-between gap-3 px-2 py-1.5">
                    <span className="text-sm text-popover-foreground">
                      {translate("KNOWLEDGEBASE/EXCLUDE_EXPIRED_DOCS")}
                    </span>
                    <Switch
                      checked={filters.excludeExpiredDocs}
                      onCheckedChange={(checked) =>
                        onFiltersChange({
                          ...filters,
                          excludeExpiredDocs: checked,
                        })
                      }
                      aria-label={translate(
                        "KNOWLEDGEBASE/EXCLUDE_EXPIRED_DOCS",
                      )}
                    />
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
        </FilterBarActions>

        {hasBulkSelection && (
          <div className="flex h-8 shrink-0 items-center gap-2 px-3">
            <span className="text-xs leading-none text-muted-foreground">
              {translate("KNOWLEDGEBASE/BULK_DELETE_COUNT", {
                count: selectedCount,
              })}
            </span>
            <Button
              variant="link"
              size="sm"
              className="h-8 px-0 text-xs"
              onClick={isAllSelectableSelected ? onDeselectAll : onSelectAll}
            >
              {isAllSelectableSelected
                ? translate("KNOWLEDGEBASE/BULK_DESELECT_ALL")
                : translate("KNOWLEDGEBASE/BULK_SELECT_ALL")}
            </Button>
            {isTaggingEnabled && onBulkTag && (
              <Button variant="outline" size="sm" onClick={onBulkTag}>
                <Tag className="size-3.5" />
                {translate("KNOWLEDGEBASE/BULK_TAG_SELECTED")}
              </Button>
            )}
            <PermissionGate permission={PERMISSIONS.KB_DELETE}>
              <Button
                variant="destructive"
                size="sm"
                onClick={onDeleteSelected}
              >
                <Trash2 className="size-3.5" />
                {translate("KNOWLEDGEBASE/BULK_DELETE_SELECTED")}
              </Button>
            </PermissionGate>
          </div>
        )}

        {hasActiveFilters && (
          <Button
            variant="outline"
            size="sm"
            className="h-8 border-yellow1 px-2 font-normal text-yellow1 hover:bg-yellow1/10 hover:text-yellow1"
            onClick={resetAll}
          >
            {translate("KNOWLEDGEBASE/FILTER_RESET_ACTION")}
            <X className="size-3.5" />
          </Button>
        )}
      </FilterBarRow>

      {isTaggingEnabled && isFiltersVisible && (
        <FilterBarRow>
          <Button
            variant="outline"
            size="icon"
            className={COMPACT_ICON_BUTTON_CLASS_NAME}
            aria-label={translate("KNOWLEDGEBASE/CATEGORY_CREATE_TRIGGER")}
            title={translate("KNOWLEDGEBASE/CATEGORY_CREATE_TRIGGER")}
            onClick={() => setIsCreateCategoryDialogOpen(true)}
          >
            <Plus className="size-4" />
          </Button>

          {/* Dynamic tag filters per category */}
          {showAllTags &&
            categories.map((category) => {
              const tagOptions: FacetedFilterOption[] = category.tags
                .filter((t) => t.isActive)
                .map((tag) => ({
                  label: getLocalizedLabel(
                    tag.displayNameEn,
                    tag.displayNameDe,
                    currentLanguage,
                  ),
                  value: tag.id,
                  onRename: (value: string) =>
                    handleRenameTag(
                      category.id,
                      tag.id,
                      tag.displayNameEn,
                      value,
                    ),
                  onDelete: () =>
                    handleDeleteTag(category.id, tag.id, tag.displayNameEn),
                }))

              return (
                <FacetedFilter
                  key={category.id}
                  title={getLocalizedLabel(
                    category.displayNameEn,
                    category.displayNameDe,
                    currentLanguage,
                  )}
                  options={tagOptions}
                  selected={filters.tags[category.id] ?? []}
                  createOptionLabel={(value) =>
                    translate("KNOWLEDGEBASE/TAG_CREATE_ACTION", { tag: value })
                  }
                  onCreateOption={(value) => handleCreateTag(category, value)}
                  isCreatingOption={isCreatingTag}
                  onSelectionChange={(values) =>
                    onFiltersChange({
                      ...filters,
                      tags: { ...filters.tags, [category.id]: values },
                    })
                  }
                />
              )
            })}

          {/* Created At */}
          <DateRangeFilter
            title={translate("KNOWLEDGEBASE/CREATED_AT_FILTER_TITLE")}
            value={filters.createdAtRange}
            onChange={(range) =>
              onFiltersChange({ ...filters, createdAtRange: range })
            }
          />
        </FilterBarRow>
      )}
    </div>
  )
}
